import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useSellerOrders } from "../hooks/useSellerOrders";
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle2,
  XCircle,
  Clock,
  Printer,
  FileText,
  ChevronRight,
  AlertCircle,
  MapPin,
  Phone,
  Mail,
  User,
} from "lucide-react";
import { Order, OrderStatus } from "../types/order.type";
import type { Buyer, ShippingAddress } from "../types/order.type";

const STATUS_CONFIG: Record<
  OrderStatus,
  {
    label: string;
    color: string;
    icon: any;
    nextStatus?: OrderStatus;
    buttonLabel?: string;
  }
> = {
  PENDING: {
    label: "Pending",
    color: "text-amber-600 bg-amber-50 border-amber-100",
    icon: Clock,
    nextStatus: "PROCESSING",
    buttonLabel: "Mark as Processing",
  },
  PROCESSING: {
    label: "Processing",
    color: "text-blue-600 bg-blue-50 border-blue-100",
    icon: Package,
    nextStatus: "SHIPPED",
    buttonLabel: "Mark as Shipped",
  },
  SHIPPED: {
    label: "Shipped",
    color: "text-indigo-600 bg-indigo-50 border-indigo-100",
    icon: Truck,
    nextStatus: "DELIVERED",
    buttonLabel: "Mark as Delivered",
  },
  DELIVERED: {
    label: "Delivered",
    color: "text-emerald-600 bg-emerald-50 border-emerald-100",
    icon: CheckCircle2,
  },
  CANCELLED: {
    label: "Cancelled",
    color: "text-rose-600 bg-rose-50 border-rose-100",
    icon: XCircle,
  },
};

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getOrderDetails, updateStatus, loading, error } = useSellerOrders();
  const [order, setOrder] = useState<Order | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchOrderDetails = async () => {
      if (!id) return;

      try {
        const data = await getOrderDetails(id);
        if (isMounted && data) {
          setOrder(data);
        }
      } catch (err) {
        console.error("Error fetching order details:", err);
      }
    };

    fetchOrderDetails();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleStatusUpdate = async () => {
    if (!order || !id || isUpdating) return;

    const nextStatus = STATUS_CONFIG[order.status]?.nextStatus;
    if (!nextStatus) return;

    setIsUpdating(true);
    try {
      const updatedOrder = await updateStatus(id, nextStatus);
      if (updatedOrder) {
        setOrder((prev) =>
          prev ? { ...prev, status: updatedOrder.status } : null,
        );
      }
    } catch (err) {
      console.error("Error updating order status:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading && !order) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent shadow-lg shadow-primary/20"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex flex-col items-center justify-center text-center p-6">
        <div className="w-16 h-16 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-600 mb-6">
          <AlertCircle size={32} />
        </div>
        <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">
          Order Not Found
        </h2>
        <p className="text-slate-500 max-w-sm mb-8">
          {error ||
            "The order you're looking for doesn't exist or has been removed."}
        </p>
        <Link
          to="/dashboard"
          className="btn btn-primary flex items-center gap-2"
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>
      </div>
    );
  }

  const statusInfo = STATUS_CONFIG[order.status] || STATUS_CONFIG.PENDING;
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 md:pb-16 bg-slate-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 md:gap-4">
            <Link
              to="/orders"
              className="p-2 bg-white border border-slate-200 rounded-xl hover:border-primary/50 hover:bg-slate-50 transition-all text-slate-400 hover:text-primary shrink-0"
            >
              <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
            </Link>
            <div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <h1 className="text-xl sm:text-2xl font-display font-bold text-slate-900">
                  Order Details
                </h1>
                <span
                  className={`px-2.5 sm:px-3 py-1 rounded-full text-[10px] sm:text-[11px] font-bold border flex items-center gap-1.5 ${statusInfo.color}`}
                >
                  <StatusIcon size={10} className="sm:w-3 sm:h-3" />
                  {statusInfo.label}
                </span>
              </div>
              <p className="text-xs sm:text-sm font-medium text-slate-400 mt-0.5">
                #{order.id}
              </p>
            </div>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <button
              className="p-2 sm:p-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-400 hover:text-slate-900 transition-all"
              title="Print Invoice"
            >
              <Printer size={18} className="sm:w-5 sm:h-5" />
            </button>
            {statusInfo.nextStatus && (
              <button
                onClick={handleStatusUpdate}
                disabled={isUpdating}
                className={`btn btn-primary shadow-lg shadow-primary/20 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base px-3 sm:px-4 py-2 ${
                  isUpdating ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isUpdating ? "Updating..." : statusInfo.buttonLabel}
                <ChevronRight size={14} className="sm:w-4 sm:h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-6 md:space-y-8">
            {/* Products List */}
            <div className="glass-card p-0 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 sm:gap-3">
                <Package
                  size={16}
                  className="sm:w-[18px] sm:h-[18px] text-secondary"
                />
                <h2 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-widest">
                  Order Items ({order.items?.length})
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                {order.items.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:gap-6 hover:bg-slate-50/20 transition-all"
                  >
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 rounded-2xl flex items-center justify-center font-bold text-slate-400 text-lg sm:text-xl shrink-0 mx-auto sm:mx-0">
                      {item.variant.product.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                        <div className="text-center sm:text-left">
                          <h3 className="font-bold text-slate-900 text-base sm:text-lg leading-tight">
                            {item.variant.product.name}
                          </h3>
                          <p className="text-xs sm:text-sm text-slate-500 mt-1">
                            {item.variant.title} - {item.variant.value}
                            {item.variant.unit}
                          </p>
                          <p className="text-[10px] sm:text-xs text-slate-400 mt-2 font-medium">
                            SKU:{" "}
                            <span className="text-slate-900">
                              {item.variant.sku}
                            </span>
                          </p>
                        </div>
                        <div className="text-center sm:text-right">
                          <p className="text-base sm:text-lg font-bold text-slate-900">
                            ₹{(item.price * item.quantity)?.toLocaleString()}
                          </p>
                          <p className="text-xs sm:text-sm text-slate-400 font-medium">
                            ₹{item.price?.toLocaleString()} x {item.quantity}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 sm:p-6 md:p-8 bg-slate-50/50 border-t border-slate-100">
                <div className="flex flex-col gap-2 sm:gap-3 w-full sm:max-w-xs ml-auto">
                  <div className="flex justify-between text-xs sm:text-sm text-slate-500">
                    <span>Subtotal</span>
                    <span className="font-bold text-slate-900 font-mono">
                      ₹{order.totalAmount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm text-slate-500">
                    <span>GST (Estimated)</span>
                    <span className="font-bold text-slate-900 font-mono">
                      ₹{order.gstAmount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm text-emerald-600 font-medium">
                    <span>Shipping</span>
                    <span className="font-bold font-mono">FREE</span>
                  </div>
                  <div className="h-px bg-slate-200 my-2" />
                  <div className="flex justify-between items-center">
                    <span className="text-sm sm:text-base text-slate-900 font-bold">
                      Total Amount
                    </span>
                    <span className="text-xl sm:text-2xl font-black text-slate-900 font-mono">
                      ₹{order.totalAmount?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline/History */}
            <div className="inner-card p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <FileText
                  size={16}
                  className="sm:w-[18px] sm:h-[18px] text-secondary"
                />
                <h2 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-widest">
                  Order Timeline
                </h2>
              </div>
              <div className="space-y-4 sm:space-y-6 relative ml-2 sm:ml-3">
                <div className="absolute left-[1px] sm:left-[1px] top-2 bottom-2 w-0.5 bg-slate-100" />
                <div className="relative flex gap-3 sm:gap-4">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-emerald-500 ring-4 ring-emerald-50 relative z-10 shrink-0 mt-0.5 sm:mt-1" />
                  <div>
                    <p className="text-sm sm:text-base font-bold text-slate-900">
                      Order Placed
                    </p>
                    <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5">
                      {formatDate(order.createdAt)}
                    </p>
                  </div>
                </div>
                {order.status !== "PENDING" && (
                  <div className="relative flex gap-3 sm:gap-4">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-500 ring-4 ring-blue-50 relative z-10 shrink-0 mt-0.5 sm:mt-1" />
                    <div>
                      <p className="text-sm sm:text-base font-bold text-slate-900">
                        Order Processed
                      </p>
                      <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5">
                        Automatically transitioned to processing
                      </p>
                    </div>
                  </div>
                )}
                {order.updatedAt !== order.createdAt && (
                  <div className="relative flex gap-3 sm:gap-4">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-indigo-500 ring-4 ring-indigo-50 relative z-10 shrink-0 mt-0.5 sm:mt-1" />
                    <div>
                      <p className="text-sm sm:text-base font-bold text-slate-900">
                        Status Updated to {order.status}
                      </p>
                      <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5">
                        {formatDate(order.updatedAt)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6 md:space-y-8">
            {/* Customer Details */}
            <div className="inner-card p-4 sm:p-6">
              <div className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 sm:mb-6">
                Customer Information
              </div>

              {/* Customer Info */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/5 text-primary flex items-center justify-center">
                    <User size={18} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Customer Name</p>
                    <p className="text-sm font-bold text-slate-900">
                      {order.buyer?.firstName} {order.buyer?.lastName}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/5 text-primary flex items-center justify-center">
                    <Mail size={18} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Email Address</p>
                    <p className="text-sm font-medium text-slate-700">
                      {order.buyer?.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/5 text-primary flex items-center justify-center">
                    <Phone size={18} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Phone Number</p>
                    <p className="text-sm font-medium text-slate-700">
                      {order.buyer?.phone || order.shippingAddress?.phone}
                    </p>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={14} className="text-primary" />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Shipping Address
                  </p>
                </div>
                {order.shippingAddress ? (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-sm font-semibold text-slate-900">
                      {order.shippingAddress.fullName ||
                        `${order.buyer?.firstName} ${order.buyer?.lastName}`}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {order.shippingAddress.address1}
                      {order.shippingAddress.address2 &&
                        `, ${order.shippingAddress.address2}`}
                    </p>
                    <p className="text-xs text-slate-600">
                      {order.shippingAddress.city},{" "}
                      {order.shippingAddress.state}
                    </p>
                    <p className="text-xs text-slate-600">
                      {order.shippingAddress.postalCode}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {order.shippingAddress.country}
                    </p>
                    {order.shippingAddress.phone && (
                      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                        <Phone size={10} /> {order.shippingAddress.phone}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 bg-slate-50 p-4 rounded-xl border border-slate-100">
                    No shipping address available
                  </p>
                )}
              </div>
            </div>

            {/* Payment Summary */}
            <div className="inner-card p-4 sm:p-6 bg-slate-900 text-white border-0 shadow-xl shadow-slate-900/10">
              <div className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 sm:mb-6">
                Payment Overview
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-slate-400">
                    Total Charged
                  </span>
                  <span className="text-base sm:text-lg font-bold font-mono">
                    ₹{order.totalAmount?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-slate-400">
                    Platform Fee
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-slate-300 font-mono">
                    ₹{order.platformFee?.toLocaleString()}
                  </span>
                </div>
                <div className="h-px bg-white/10 my-1 sm:my-2" />
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-[9px] sm:text-[10px] font-bold text-primary-light uppercase tracking-widest mb-1">
                      Your Net Earning
                    </p>
                    <span className="text-xl sm:text-2xl font-black font-mono">
                      ₹
                      {(
                        order.totalAmount - order.platformFee
                      )?.toLocaleString()}
                    </span>
                  </div>
                  <span className="px-1.5 sm:px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-[9px] sm:text-[10px] font-bold uppercase border border-emerald-500/20 mb-1">
                    Paid
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
